// web/src/pages/AdminComptes.jsx
import React, { useEffect, useState } from "react";
import { apiFetch } from "../utils/api";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const API = {
  whoami: `/api/admin/whoami`,
  list: (q, limit = 50, offset = 0) =>
    `/api/admin/users?q=${encodeURIComponent(q || "")}&limit=${limit}&offset=${offset}`,
  detail: (id) => `/api/admin/users/${encodeURIComponent(id)}`,
  create: `/api/admin/users`,
  profile: (id) => `/api/admin/users/${encodeURIComponent(id)}/profile`,
  password: (id) => `/api/admin/users/${encodeURIComponent(id)}/password`,
  subscription: (id) => `/api/admin/users/${encodeURIComponent(id)}/subscription`,
  delete: (id) => `/api/admin/users/${encodeURIComponent(id)}`,
};

const PAGE_SIZE = 50;

function fmtDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleString("fr-FR");
  } catch {
    return String(d);
  }
}

function toInputValue(d) {
  if (!d) return "";
  try {
    const dt = new Date(d);
    const pad = (n) => String(n).padStart(2, "0");
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(
      dt.getHours()
    )}:${pad(dt.getMinutes())}`;
  } catch {
    return "";
  }
}

function fromInputValue(v) {
  if (!v) return null;
  try {
    return new Date(v).toISOString();
  } catch {
    return null;
  }
}

function PlanBadge({ sub_status }) {
  const up = (sub_status || "NONE").toUpperCase();
  const color =
    up === "PRO" ? "#16a34a" : up === "NORMAL" ? "#2563eb" : "#64748b";
  const label = up === "PRO" ? "Pro" : up === "NORMAL" ? "Normal" : "Aucun";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 8px",
        borderRadius: 999,
        background: `${color}22`,
        color,
        fontWeight: 800,
      }}
    >
      <span
        style={{ width: 8, height: 8, borderRadius: "50%", background: color }}
      />
      {label}
    </span>
  );
}

export default function AdminComptes() {
  const [authChecked, setAuthChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [whoErr, setWhoErr] = useState("");

  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);

  // pagination
  const [page, setPage] = useState(0);

  // ---- Create user UI state
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createdPassword, setCreatedPassword] = useState(""); // returned once by API

  const [nu, setNu] = useState({
    name: "",
    email: "",
    email_verified: true, // skip verification by default
    want_manual_pwd: false,
    password: "",
    sub_status: "NONE",
    monthly_limit: 0,
    reports_remaining: 0,
    sub_started_at: "",
    sub_ends_at: "",
  });

  // Admin check
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await apiFetch(API.whoami);
        if (!r.ok) {
          setIsAdmin(false);
          setWhoErr(await r.text());
        } else {
          const data = await r.json();
          setIsAdmin(!!data.is_admin);
        }
      } catch (e) {
        setIsAdmin(false);
        setWhoErr(String(e));
      } finally {
        if (alive) setAuthChecked(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const load = async (pageOverride) => {
    const nextPage =
      typeof pageOverride === "number" && pageOverride >= 0
        ? pageOverride
        : page;
    const offset = nextPage * PAGE_SIZE;

    setLoading(true);
    setErr("");
    try {
      const r = await apiFetch(API.list(q, PAGE_SIZE, offset));
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(nextPage);
    } catch (e) {
      setErr(String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authChecked && isAdmin) {
      load(0);
    }

  }, [authChecked, isAdmin]);

  const openUser = async (id) => {
    setBusy(true);
    setErr("");
    try {
      const r = await apiFetch(API.detail(id));
      if (!r.ok) throw new Error(await r.text());
      const u = await r.json();

      // Normalize from either flat columns or nested "subscription"
      const sub_status = (u.sub_status || u.subscription?.plan || "NONE")
        .toString()
        .toUpperCase()
        .replace("PRO", "PRO")
        .replace("NORMAL", "NORMAL")
        .replace("NONE", "NONE");
      const monthly_limit =
        (u.monthly_limit ?? u.subscription?.reports_quota) ?? 0;
      const reports_remaining =
        (u.reports_remaining ?? u.subscription?.reports_left) ?? 0;
      const sub_started_at =
        u.sub_started_at ?? u.subscription?.period_start ?? null;
      const sub_ends_at = u.sub_ends_at ?? u.subscription?.period_end ?? null;

      setSel({
        ...u,
        edit: {
          name: u.name || "",
          email: u.email || "",
          email_verified: !!u.email_verified,
          password: "",
          sub_status,
          monthly_limit,
          reports_remaining,
          sub_started_at: toInputValue(sub_started_at),
          sub_ends_at: toInputValue(sub_ends_at),
        },
      });
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const saveProfile = async () => {
    if (!sel) return;
    setBusy(true);
    setErr("");
    try {
      const body = {
        name: sel.edit.name,
        email: sel.edit.email,
        email_verified: sel.edit.email_verified,
      };
      const r = await apiFetch(API.profile(sel.id), {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(await r.text());
      await openUser(sel.id);
      await load(page);
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const savePassword = async () => {
    if (!sel || !sel.edit.password || sel.edit.password.length < 8) {
      alert("Mot de passe trop court (min 8).");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const r = await apiFetch(API.password(sel.id), {
        method: "POST",
        body: JSON.stringify({ new_password: sel.edit.password }),
      });
      if (!r.ok) throw new Error(await r.text());
      setSel((prev) =>
        prev ? { ...prev, edit: { ...prev.edit, password: "" } } : prev
      );
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  // ⬇️ This posts exactly the columns /api/me/quota reads.
  const saveSubscription = async () => {
    if (!sel) return;
    setBusy(true);
    setErr("");

    const payload = {
      sub_status: (sel.edit.sub_status || "NONE").toUpperCase(), // "PRO"|"NORMAL"|"NONE"
      monthly_limit: Number(sel.edit.monthly_limit || 0),
      reports_remaining: Number(sel.edit.reports_remaining || 0),
      sub_started_at: fromInputValue(sel.edit.sub_started_at),
      sub_ends_at: fromInputValue(sel.edit.sub_ends_at),
    };

    try {
      const r = await apiFetch(API.subscription(sel.id), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(await r.text());
      await openUser(sel.id);
      await load(page);
      try {
        window.dispatchEvent(new Event("quota-changed"));
      } catch {
        // ignore
      }
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const deleteUser = async () => {
    if (!sel) return;
    if (!window.confirm(`Supprimer définitivement l’utilisateur ${sel.email} ?`))
      return;
    setBusy(true);
    setErr("");
    try {
      const r = await apiFetch(API.delete(sel.id), { method: "DELETE" });
      if (!r.ok) throw new Error(await r.text());
      setSel(null);
      // If we deleted the last user on the page, go back one page if possible
      const newTotal = total - 1;
      const totalPages = Math.max(1, Math.ceil(newTotal / PAGE_SIZE));
      const nextPage = Math.min(page, totalPages - 1);
      setTotal(newTotal);
      await load(nextPage);
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const planPreset = (plan) => {
    if (plan === "PRO") return { monthly_limit: 200, reports_remaining: 200 };
    if (plan === "NORMAL") return { monthly_limit: 20, reports_remaining: 20 };
    return { monthly_limit: 0, reports_remaining: 0 };
  };

  const doCreate = async () => {
    if (!nu.email || !nu.email.includes("@")) {
      alert("Email invalide.");
      return;
    }
    if (nu.want_manual_pwd && nu.password.length < 8) {
      alert("Mot de passe trop court (min 8).");
      return;
    }

    setCreating(true);
    setErr("");
    setCreatedPassword("");
    try {
      const payload = {
        email: nu.email.trim(),
        name: (nu.name || "").trim(),
        email_verified: !!nu.email_verified,
        password: nu.want_manual_pwd ? nu.password : "", // server will auto-generate if <8
        sub_status: (nu.sub_status || "NONE").toUpperCase(),
        monthly_limit: Number(nu.monthly_limit || 0),
        reports_remaining: Number(nu.reports_remaining || 0),
        sub_started_at: fromInputValue(nu.sub_started_at),
        sub_ends_at: fromInputValue(nu.sub_ends_at),
      };
      const r = await apiFetch(API.create, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();

      // Show returned password once (copy it!)
      if (data.plain_password) setCreatedPassword(String(data.plain_password));
      setShowCreate(false);

      // Refresh & open new user (go back to first page to see latest, or keep page 0?)
      await load(0);
      if (data.user?.id) await openUser(data.user.id);

      // Reset form
      setNu({
        name: "",
        email: "",
        email_verified: true,
        want_manual_pwd: false,
        password: "",
        sub_status: "NONE",
        monthly_limit: 0,
        reports_remaining: 0,
        sub_started_at: "",
        sub_ends_at: "",
      });
    } catch (e) {
      setErr(String(e));
    } finally {
      setCreating(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Guards
  if (!authChecked) {
    return (
      <div className="results-page">
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "28px 16px 64px" }}>
          <div className="glass-chip">Vérification…</div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="results-page">
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <style>{`
          :root{ --bg1:#3b82f6; --bg2:#1e40af; --surface:rgba(255,255,255,0.95); --ink:#0f172a; }
          .results-page{ min-height:100vh; background: linear-gradient(135deg,var(--bg1) 0%, var(--bg2) 100%); font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen,Ubuntu,Cantarell,sans-serif; color:var(--ink); }
          .wrap{ max-width: 900px; margin: 0 auto; padding: 28px 16px 64px; }
          .card{ background: var(--surface); backdrop-filter: blur(20px); border: 1px solid rgba(255,255,255,0.22); border-radius: 18px; padding: 16px; box-shadow: 0 16px 36px rgba(0,0,0,0.12); margin-bottom: 16px; }
          .glass-chip{ background: var(--surface); border: 1px solid rgba(255,255,255,0.22); border-radius: 14px; padding: 8px 10px; }
        `}</style>
        <div className="wrap">
          <div className="card">
            <h2 style={{ marginTop: 0 }}>Accès refusé</h2>
            <p>Vous devez être connecté avec un compte administrateur.</p>
            {whoErr && (
              <pre style={{ whiteSpace: "pre-wrap" }}>{String(whoErr)}</pre>
            )}
            <div style={{ marginTop: 10 }}>
              <Link to="/" className="glass-chip">
                ← Retour
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // UI
  return (
    <div className="results-page">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <style>{`
        :root{ --bg1:#3b82f6; --bg2:#1e40af; --surface:rgba(255,255,255,0.95); --ink:#0f172a; --muted:#64748b; }
        .results-page{ min-height:100vh; background: linear-gradient(135deg,var(--bg1) 0%, var(--bg2) 100%); font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen,Ubuntu,Cantarell,sans-serif; color:var(--ink); }
        .wrap{ max-width: 1100px; margin: 0 auto; padding: 28px 16px 64px; }
        .title{ color:#fff; font-size: clamp(28px,4vw,40px); font-weight:900; text-shadow: 0 3px 18px rgba(0,0,0,.28); margin: 6px 0 12px; }
        .row{ display:flex; gap:12px; align-items:center; flex-wrap: wrap; }
        .card{ background: var(--surface); backdrop-filter: blur(20px); border: 1px solid rgba(255,255,255,0.22); border-radius: 18px; padding: 16px; box-shadow: 0 16px 36px rgba(0,0,0,0.12); margin-bottom: 16px; }
        .glass-chip{ background: var(--surface); border: 1px solid rgba(255,255,255,0.22); border-radius: 14px; padding: 8px 10px; }
        .btn{ display:inline-flex; align-items:center; gap:8px; padding:10px 14px; border-radius:10px; border:0; cursor:pointer; background:linear-gradient(135deg,#3b82f6,#1d4ed8); color:#fff; font-weight:800; }
        .btn.alt{ background:#f3f4f6; color:#0f172a; border:1px solid #e5e7eb; }
        .btn.danger{ background:linear-gradient(135deg,#ef4444,#b91c1c); }
        input, select{ width:100%; padding:8px 10px; border-radius:10px; border:1px solid #e5e7eb; background:#fff; }
        label{ font-weight:700; display:block; margin-top:8px; }
        .tr{ background: var(--surface); border:1px solid rgba(0,0,0,0.06); border-radius:12px; }
        .mono{ font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; }
      `}</style>

      <div className="wrap">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <div className="glass-chip">
            <Link to="/">← Retour</Link>
          </div>
          <div className="title">Admin · Comptes</div>
        </div>

        {/* Search + Create */}
        <div className="card" style={{ display: "grid", gap: 12 }}>
          <div
            className="row"
            style={{ alignItems: "stretch", justifyContent: "space-between" }}
          >
            <div className="row" style={{ flex: 1, minWidth: 260 }}>
              <input
                placeholder="Rechercher (email, nom)"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") load(0);
                }}
              />
              <button
                className="btn"
                onClick={() => load(0)}
                disabled={loading}
              >
                {loading ? "Chargement…" : "Rechercher"}
              </button>
            </div>
            <div>
              <button
                className="btn alt"
                onClick={() => setShowCreate((v) => !v)}
              >
                {showCreate ? "Annuler" : "Créer un compte"}
              </button>
            </div>
          </div>

          {showCreate && (
            <div className="card" style={{ margin: 0 }}>
              <div
                style={{
                  fontSize: 12,
                  color: "#64748b",
                  marginBottom: 6,
                }}
              >
                Créer un compte (pré-configuré)
              </div>

              <div
                className="row"
                style={{
                  gap: 14,
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                }}
              >
                {/* Col 1: Identity */}
                <div style={{ flex: "1 1 320px" }}>
                  <label>Nom</label>
                  <input
                    value={nu.name}
                    onChange={(e) =>
                      setNu((p) => ({ ...p, name: e.target.value }))
                    }
                  />
                  <label>Email</label>
                  <input
                    type="email"
                    value={nu.email}
                    onChange={(e) =>
                      setNu((p) => ({ ...p, email: e.target.value }))
                    }
                  />
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontWeight: 600,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!nu.email_verified}
                      onChange={(e) =>
                        setNu((p) => ({
                          ...p,
                          email_verified: e.target.checked,
                        }))
                      }
                      style={{ width: 16, height: 16 }}
                    />
                    Marquer l’email comme vérifié (pas de vérification)
                  </label>
                </div>

                {/* Col 2: Password */}
                <div style={{ flex: "1 1 280px" }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontWeight: 600,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!nu.want_manual_pwd}
                      onChange={(e) =>
                        setNu((p) => ({
                          ...p,
                          want_manual_pwd: e.target.checked,
                        }))
                      }
                      style={{ width: 16, height: 16 }}
                    />
                    Définir un mot de passe manuellement
                  </label>
                  <label>Mot de passe (min 8)</label>
                  <input
                    type="password"
                    value={nu.password}
                    disabled={!nu.want_manual_pwd}
                    onChange={(e) =>
                      setNu((p) => ({ ...p, password: e.target.value }))
                    }
                  />
                  <div
                    style={{
                      fontSize: 12,
                      color: "#64748b",
                      marginTop: 6,
                    }}
                  >
                    Si décoché, un mot de passe sera généré automatiquement et
                    affiché une seule fois.
                  </div>
                </div>

                {/* Col 3: Subscription */}
                <div style={{ flex: "1 1 360px" }}>
                  <label>Plan</label>
                  <select
                    value={nu.sub_status}
                    onChange={(e) => {
                      const plan = e.target.value;
                      const preset = planPreset(plan.toUpperCase());
                      setNu((p) => ({
                        ...p,
                        sub_status: plan,
                        monthly_limit: preset.monthly_limit,
                        reports_remaining: preset.reports_remaining,
                      }));
                    }}
                  >
                    <option value="NONE">Aucun</option>
                    <option value="NORMAL">NORMAL (20/mois)</option>
                    <option value="PRO">PRO (200/mois)</option>
                  </select>

                  <div className="row" style={{ marginTop: 8 }}>
                    <div style={{ flex: 1 }}>
                      <label>Quota (mois)</label>
                      <input
                        type="number"
                        min="0"
                        value={nu.monthly_limit}
                        onChange={(e) =>
                          setNu((p) => ({
                            ...p,
                            monthly_limit: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label>Restants</label>
                      <input
                        type="number"
                        min="0"
                        value={nu.reports_remaining}
                        onChange={(e) =>
                          setNu((p) => ({
                            ...p,
                            reports_remaining: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>

                  <label style={{ marginTop: 8 }}>Début de période</label>
                  <input
                    type="datetime-local"
                    value={nu.sub_started_at}
                    onChange={(e) =>
                      setNu((p) => ({
                        ...p,
                        sub_started_at: e.target.value,
                      }))
                    }
                  />

                  <label>Fin de période</label>
                  <input
                    type="datetime-local"
                    value={nu.sub_ends_at}
                    onChange={(e) =>
                      setNu((p) => ({
                        ...p,
                        sub_ends_at: e.target.value,
                      }))
                    }
                  />

                  <div style={{ marginTop: 10 }}>
                    <button
                      type="button"
                      className="btn"
                      disabled={creating}
                      onClick={doCreate}
                    >
                      {creating ? "…" : "Créer le compte"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {createdPassword && (
            <div className="glass-chip" style={{ marginTop: 8 }}>
              <strong>Mot de passe initial :</strong>{" "}
              <span className="mono">{createdPassword}</span>
              <span
                style={{
                  marginLeft: 8,
                  fontSize: 12,
                  color: "#64748b",
                }}
              >
                (copiez-le maintenant — il ne sera plus visible ensuite)
              </span>
            </div>
          )}
        </div>

        {err && (
          <div
            className="card"
            style={{
              color: "#7f1d1d",
              background: "#fee2e2",
              border: "1px solid #fecaca",
            }}
          >
            {String(err)}
          </div>
        )}

        {/* List */}
        <div className="card">
          <div
            className="row"
            style={{ gap: 0, fontWeight: 800, color: "#475569" }}
          >
            <div style={{ flex: "1 1 220px" }}>Utilisateur</div>
            <div style={{ flex: "1 1 200px" }}>Email</div>
            <div style={{ width: 90 }}>Vérifié</div>
            <div style={{ width: 140 }}>Plan</div>
            <div style={{ width: 130, textAlign: "right" }}>Restants</div>
            <div style={{ width: 180 }}>Fin de période</div>
            <div style={{ width: 110 }}></div>
          </div>
          {items.map((u) => {
            const sub_status = (
              u.sub_status || u.subscription?.plan || "NONE"
            )
              .toString()
              .toUpperCase();
            const left =
              (u.reports_remaining ?? u.subscription?.reports_left) ?? 0;
            const quota =
              (u.monthly_limit ?? u.subscription?.reports_quota) ?? 0;
            const end = u.sub_ends_at || u.subscription?.period_end;
            return (
              <div
                key={u.id}
                className="row tr"
                style={{
                  gap: 0,
                  alignItems: "stretch",
                  marginTop: 8,
                }}
              >
                <div style={{ flex: "1 1 220px" }}>
                  <strong>{u.name || "—"}</strong>
                </div>
                <div style={{ flex: "1 1 200px" }}>{u.email}</div>
                <div style={{ width: 90 }}>
                  {u.email_verified ? "✅" : "❌"}
                </div>
                <div style={{ width: 140 }}>
                  <PlanBadge sub_status={sub_status} />
                </div>
                <div style={{ width: 130, textAlign: "right" }}>
                  {left} / {quota}
                </div>
                <div style={{ width: 180 }}>{fmtDate(end)}</div>
                <div
                  style={{
                    width: 110,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    className="btn alt"
                    onClick={() => openUser(u.id)}
                  >
                    Éditer
                  </button>
                </div>
              </div>
            );
          })}

          {/* Pagination controls */}
          <div
            className="row"
            style={{
              marginTop: 12,
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{
                fontSize: 12,
                color: "#64748b",
              }}
            >
              {total} utilisateur{total > 1 ? "s" : ""} • Page {page + 1} /{" "}
              {totalPages}
            </div>
            <div className="row" style={{ gap: 8 }}>
              <button
                className="btn alt"
                disabled={page <= 0 || loading}
                onClick={() => load(page - 1)}
              >
                ← Précédent
              </button>
              <button
                className="btn alt"
                disabled={page + 1 >= totalPages || loading}
                onClick={() => load(page + 1)}
              >
                Suivant →
              </button>
            </div>
          </div>
        </div>

        {/* Edit panel */}
        {sel && (
          <div className="card">
            <div className="row" style={{ justifyContent: "space-between" }}>
              <div>
                <strong>Édition :</strong> {sel.name}{" "}
                <span className="glass-chip">{sel.email}</span>
              </div>
              <div className="row">
                <button
                  className="btn alt"
                  onClick={() => setSel(null)}
                >
                  Fermer
                </button>
                <button
                  className="btn danger"
                  onClick={deleteUser}
                  disabled={busy}
                >
                  Supprimer
                </button>
              </div>
            </div>

            <div
              className="row"
              style={{
                gap: 14,
                alignItems: "flex-start",
                marginTop: 10,
                flexWrap: "wrap",
              }}
            >
              {/* Profil */}
              <div
                style={{ flex: "1 1 340px" }}
                className="card"
              >
                <div
                  style={{
                    fontSize: 12,
                    color: "#64748b",
                    marginBottom: 6,
                  }}
                >
                  Profil
                </div>
                <label>Nom</label>
                <input
                  value={sel.edit.name}
                  onChange={(e) =>
                    setSel((p) => ({
                      ...p,
                      edit: { ...p.edit, name: e.target.value },
                    }))
                  }
                />
                <label>Email</label>
                <input
                  type="email"
                  value={sel.edit.email}
                  onChange={(e) =>
                    setSel((p) => ({
                      ...p,
                      edit: { ...p.edit, email: e.target.value },
                    }))
                  }
                />
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontWeight: 600,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!sel.edit.email_verified}
                    onChange={(e) =>
                      setSel((p) => ({
                        ...p,
                        edit: {
                          ...p.edit,
                          email_verified: e.target.checked,
                        },
                      }))
                    }
                    style={{ width: 16, height: 16 }}
                  />
                  Email vérifié
                </label>
                <div
                  style={{
                    marginTop: 10,
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    type="button"
                    className="btn"
                    disabled={busy}
                    onClick={saveProfile}
                  >
                    {busy ? "…" : "Enregistrer le profil"}
                  </button>
                </div>
              </div>

              {/* Password */}
              <div
                style={{ flex: "0 1 300px" }}
                className="card"
              >
                <div
                  style={{
                    fontSize: 12,
                    color: "#64748b",
                    marginBottom: 6,
                  }}
                >
                  Mot de passe
                </div>
                <label>Nouveau mot de passe</label>
                <input
                  type="password"
                  value={sel.edit.password}
                  onChange={(e) =>
                    setSel((p) => ({
                      ...p,
                      edit: { ...p.edit, password: e.target.value },
                    }))
                  }
                />
                <div style={{ marginTop: 10 }}>
                  <button
                    type="button"
                    className="btn"
                    disabled={busy}
                    onClick={savePassword}
                  >
                    {busy ? "…" : "Mettre à jour le MDP"}
                  </button>
                </div>
              </div>

              {/* Subscription */}
              <div
                style={{ flex: "1 1 360px" }}
                className="card"
              >
                <div
                  style={{
                    fontSize: 12,
                    color: "#64748b",
                    marginBottom: 6,
                  }}
                >
                  Abonnement
                </div>

                <label>Plan</label>
                <select
                  value={sel.edit.sub_status}
                  onChange={(e) =>
                    setSel((p) => ({
                      ...p,
                      edit: { ...p.edit, sub_status: e.target.value },
                    }))
                  }
                >
                  <option value="NONE">Aucun</option>
                  <option value="NORMAL">NORMAL (20/mois)</option>
                  <option value="PRO">PRO (200/mois)</option>
                </select>

                <div className="row" style={{ marginTop: 8 }}>
                  <div style={{ flex: 1 }}>
                    <label>Quota (mois)</label>
                    <input
                      type="number"
                      min="0"
                      value={sel.edit.monthly_limit}
                      onChange={(e) =>
                        setSel((p) => ({
                          ...p,
                          edit: {
                            ...p.edit,
                            monthly_limit: e.target.value,
                          },
                        }))
                      }
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label>Restants</label>
                    <input
                      type="number"
                      min="0"
                      value={sel.edit.reports_remaining}
                      onChange={(e) =>
                        setSel((p) => ({
                          ...p,
                          edit: {
                            ...p.edit,
                            reports_remaining: e.target.value,
                          },
                        }))
                      }
                    />
                  </div>
                </div>

                <label style={{ marginTop: 8 }}>Début de période</label>
                <input
                  type="datetime-local"
                  value={sel.edit.sub_started_at || ""}
                  onChange={(e) =>
                    setSel((p) => ({
                      ...p,
                      edit: {
                        ...p.edit,
                        sub_started_at: e.target.value,
                      },
                    }))
                  }
                />

                <label>Fin de période</label>
                <input
                  type="datetime-local"
                  value={sel.edit.sub_ends_at || ""}
                  onChange={(e) =>
                    setSel((p) => ({
                      ...p,
                      edit: {
                        ...p.edit,
                        sub_ends_at: e.target.value,
                      },
                    }))
                  }
                />

                <div style={{ marginTop: 10 }}>
                  <button
                    type="button"
                    className="btn"
                    disabled={busy}
                    onClick={saveSubscription}
                  >
                    {busy ? "…" : "Enregistrer l’abonnement"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
